import React, { useState, useEffect, useRef } from "react";
import {
  getFirestore,
  collection,
  doc,
  onSnapshot,
  getDoc,
} from "firebase/firestore";
import { useLocation } from "react-router-dom";
import ContentBox from "./content/ContentBox/contentbox";
import SubmissionSection from "./content/submission/DataSubmissionModal"; 

const DailyCourseModule = () => {
  // ==================== STATE MANAGEMENT ====================
  const location = useLocation();
  const state = location.state;
  const assignmentListRef = useRef(null);

  const [assignments, setAssignments] = useState([]);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [selectedTab, setSelectedTab] = useState("Video");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [isAssignmentListSticky, setIsAssignmentListSticky] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
 
  // Extract IDs from state
  const dayId = state?.dayId;
  const courseId = state?.courseId;
  const dayTitle = selectedDay?.dayTitle || "";

  // Check if current assignment is the last one
  const isLastAssignment = selectedAssignment && assignments.length > 0 && 
    selectedAssignment.id === assignments[assignments.length - 1]?.id;

  // ==================== DATA FETCHING ====================
  useEffect(() => {
    // Validation
    if (!dayId || !courseId) {
      setError("Course or day information is missing");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    // Fetch day information
    const fetchDayInfo = async () => {
      try {
        const db = getFirestore();
        const dayRef = doc(db, `courses/${courseId}/day/${dayId}`);
        const dayDoc = await getDoc(dayRef);

        if (dayDoc.exists()) {
          setSelectedDay(dayDoc.data());
        } else {
          setError("Day information not found");
        }
      } catch (err) {
        setError("Failed to load day information: " + err.message);
      }
    };

    fetchDayInfo();

    // Set up real-time listener for assignments
    const fetchAssignments = async () => {
      try {
        const db = getFirestore();
        const path = `courses/${courseId}/day/${dayId}/assignments`;
        const assignmentsRef = collection(db, path);

        const unsubscribe = onSnapshot(
          assignmentsRef,
          (snapshot) => {
            const assignmentsData = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            setAssignments(assignmentsData);

            // Auto-select first assignment if none selected
            if (!selectedAssignment && assignmentsData.length > 0) {
              setSelectedAssignment(assignmentsData[0]);
            } else if (selectedAssignment) {
              // Update selected assignment if it changed
              const updatedAssignment = assignmentsData.find(
                (a) => a.id === selectedAssignment.id
              );
              if (
                updatedAssignment &&
                JSON.stringify(updatedAssignment) !==
                  JSON.stringify(selectedAssignment)
              ) {
                setSelectedAssignment(updatedAssignment);
              }
            }

            setIsLoading(false);
          },
          (err) => {
            setError("Failed to load assignments: " + err.message);
            setIsLoading(false);
          }
        );

        return () => unsubscribe();
      } catch (err) {
        setError("Failed to initialize assignments: " + err.message);
        setIsLoading(false);
      }
    };

    fetchAssignments();
  }, [dayId, courseId, selectedAssignment]);

  // ==================== EVENT HANDLERS ====================
  const handleAssignmentSelect = (assignment) => {
    setSelectedAssignment(assignment);

    // Auto-select appropriate tab based on available content
    if (assignment.videoLink) {
      setSelectedTab("Video");
    } else if (assignment.description) {
      setSelectedTab("Description");
    } else if (assignment.fileURL) {
      setSelectedTab("Materials");
    } else if (assignment.program) {
      setSelectedTab("Program");
    }
  };

  // ==================== ERROR & LOADING STATES ====================
  if (error) {
    return (
      <div
        style={{
          minHeight: "100vh",
          padding: "16px 12px",
          fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
          maxWidth: "1600px",
          margin: "0 auto",
        }}
      >
        <PageHeader breadcrumb="Course / Error" title="Error Loading Course" />
        <div style={{ color: "#dc3545", padding: "20px", textAlign: "center" }}>
          {error}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          padding: "16px 12px",
          fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
          maxWidth: "1600px",
          margin: "0 auto",
        }}
      >
        <PageHeader breadcrumb="Course / Loading..." title="Loading..." />
      </div>
    );
  }

  // ==================== MAIN RENDER ====================
  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "16px 12px",
        fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
        maxWidth: "1600px",
        margin: "0 auto",
      }}
    >
      {/* Header Section */}
      <PageHeader
        breadcrumb={buildBreadcrumb(dayId, selectedAssignment)}
        title={dayTitle}
      />

      {/* Main Content Layout */}
      <div
        style={{
          display: "flex",
          gap: "24px",
          flexWrap: "nowrap", // Prevent wrapping to keep containers side by side
          margin: "0 auto",
          maxWidth: "1700px",
          minHeight: "800px", // Ensure minimum height
          overflow: "hidden", // Prevent horizontal overflow
        }}
      >
        {/* Left Section - Assignment List */}
        <div
          style={{
            flex: "0 0 400px", // Fixed width, no grow/shrink
            minWidth: "400px",
            maxWidth: "400px",
          }}
        >
          <AssignmentsList
            assignments={assignments}
            selectedAssignment={selectedAssignment}
            onAssignmentSelect={handleAssignmentSelect}
            isSticky={isAssignmentListSticky}
            ref={assignmentListRef}
          />
        </div>

        {/* Right Section - Content and Submission */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            flex: "1",
            minWidth: "600px", // Ensure minimum width for content
            maxWidth: "calc(100% - 424px)", // Reserve space for assignment list
          }}>
          <ContentSection
            selectedAssignment={selectedAssignment}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            dayId={dayId}
            courseId={courseId}
            isLastAssignment={isLastAssignment}
            showTooltip={showTooltip}
            setShowTooltip={setShowTooltip}
          />
        </div>
      </div>
    </div>
  );
};

// ==================== COMPONENT SECTIONS ====================

// Page Header Component
const PageHeader = ({ breadcrumb, title }) => (
  <div
    style={{
     background: "linear-gradient(90deg, #6366f1 0%, #a21caf 100%)",
      padding: "16px 24px",
      margin: "0 auto 32px auto",
      maxWidth: "100%",
      width: "100%",
      borderRadius: "12px",
      boxShadow: "0 4px 32px 0 #e0e7ff",
      marginBottom: "24px",
    }}
  >
    <div
      style={{
        fontSize: "0.9rem",
        color: "#cccccc",
        marginBottom: "8px",
      }}
    >
      {breadcrumb}
    </div>
    <h1
      style={{
        margin: 0,
        fontSize: "1.8rem",
        color: "#eeeeee",
        fontWeight: "700",
      }}
    >
      {title}
    </h1>
  </div>
);

// Content Section Component - Updated with flexible height
const ContentSection = ({ 
  selectedAssignment, 
  selectedTab, 
  setSelectedTab, 
  dayId, 
  courseId, 
  isLastAssignment,
  showTooltip,
  setShowTooltip 
}) => (
  <div
    style={{
      width: "100%", // Take full width of parent container
      background: "#ffffff",
      padding: "24px 20px",
      minHeight: "800px", // Set consistent minimum height
      height: "800px", // Fixed height to match assignment list
      display: "flex",
      flexDirection: "column",
      border: "1.5px ",
      borderRadius: "8px",
      boxShadow: "0 8px 24px rgba(222, 216, 216, 0.08)",
      transition: "box-shadow 0.2s",
      overflow: "hidden", // Prevent content overflow
    }}
  >
    {/* Assignment Title Header */}
    {selectedAssignment && (
      <div
        style={{
          height: "130px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "-20px",
          paddingBottom: "16px",
          borderBottom: "2px solid #f8f9fa",
          background: "linear-gradient(90deg, #6366f1 0%, #a21caf 100%)",
          padding: "16px",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          borderRadius: "8px"
        }}
      >
        <div>
          <span
            style={{
              fontSize: "1.5rem",
              fontWeight: "600",
              color: "#ffffff",
              textAlign: "center",
              textTransform: "uppercase",
              marginBottom: "16px",
            }}
          >
            {selectedAssignment.title}
          </span>
        </div>
      </div>
    )}

    {/* Content Navigation Tabs */}
    <ContentTabs
      selectedAssignment={selectedAssignment}
      selectedTab={selectedTab}
      setSelectedTab={setSelectedTab}
      isLastAssignment={isLastAssignment}
      showTooltip={showTooltip}
      setShowTooltip={setShowTooltip}
    />

    {/* Content Display Area - Updated with flexible sizing */}
    <div style={{ 
      flex: 1, 
      minHeight: "400px",
      height: "calc(100% - 200px)", // Calculate height minus header and tabs
      overflow: "hidden"
    }}>
      {selectedTab === "Submit" ? (
        <SubmissionSection dayId={dayId} courseId={courseId} />
      ) : (
        <ContentBox 
          selectedAssignment={selectedAssignment} 
          selectedTab={selectedTab} 
          style={{
            height: "100%", // Make ContentBox fill available space
            width: "100%",
            padding: '20px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto', // Allow scrolling if content is too long
            boxSizing: 'border-box'
          }}
        />
      )}
    </div>
  </div>
);

// Content Tabs Component
const ContentTabs = ({ 
  selectedAssignment, 
  selectedTab, 
  setSelectedTab, 
  isLastAssignment,
  showTooltip,
  setShowTooltip 
}) => (
  <div
    style={{
      borderBottom: "2px solid rgb(0, 0, 0)",
      marginBottom: "20px",
      paddingBottom: "12px",
      display: "flex",
      gap: "32px",
    }}
  >
    {selectedAssignment?.videoLink && (
      <TabButton
        isActive={selectedTab === "Video"}
        onClick={() => setSelectedTab("Video")}
        icon="bi-play-circle"
        label="Video"
      />
    )}
    {selectedAssignment?.description && (
      <TabButton
        isActive={selectedTab === "Description"}
        onClick={() => setSelectedTab("Description")}
        icon="bi-file-text"
        label="Description"
      />
    )}
    {selectedAssignment?.fileURL && (
      <TabButton
        isActive={selectedTab === "Materials"}
        onClick={() => setSelectedTab("Materials")}
        icon="bi-folder"
        label="Materials"
      />
    )}
    {selectedAssignment?.program && (
      <TabButton
        isActive={selectedTab === "Program"}
        onClick={() => setSelectedTab("Program")}
        icon="bi-code-slash"
        label="Program"
      />
    )}
    
    {/* Submit Button with Conditional Logic */}
    <div style={{ position: 'relative', marginLeft: 'auto' }}>
      <TabButton
        isActive={selectedTab === "Submit"}
        onClick={() => isLastAssignment && setSelectedTab("Submit")}
        icon="bi-upload"
        label="Submit"
        disabled={!isLastAssignment}
        onMouseEnter={() => !isLastAssignment && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          opacity: isLastAssignment ? 1 : 0.5,
          cursor: isLastAssignment ? "pointer" : "not-allowed",
          backgroundColor: isLastAssignment ? "#e6e6fa" : "#f5f5f5",
          color: isLastAssignment ? (selectedTab === "Submit" ? "#7c3aed" : "#a0aec0") : "#999"
        }}
      />
      
      {/* Tooltip for disabled submit button */}
      {showTooltip && !isLastAssignment && (
        <div
          style={{
            position: 'absolute',
            top: '-50px',
            right: '0',
            backgroundColor: '#2d3748',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '0.875rem',
            whiteSpace: 'nowrap',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '100%',
              right: '20px',
              border: '5px solid transparent',
              borderTopColor: '#2d3748'
            }
          }}
        >
          💭 First complete all content modules
          <div
            style={{
              position: 'absolute',
              top: '100%',
              right: '20px',
              width: 0,
              height: 0,
              borderLeft: '5px solid transparent',
              borderRight: '5px solid transparent',
              borderTop: '5px solid #2d3748'
            }}
          />
        </div>
      )}
    </div>
  </div>
);

// Tab Button Component
const TabButton = ({ 
  isActive, 
  onClick, 
  icon, 
  label, 
  disabled = false, 
  onMouseEnter,
  onMouseLeave,
  style = {} 
}) => (
  <span
    onClick={!disabled ? onClick : undefined}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    style={{
      fontWeight: "600",
      color: disabled ? "#999" : (isActive ? "#7c3aed" : "#a0aec0"),
      cursor: disabled ? "not-allowed" : "pointer",
      paddingBottom: "4px",
      transition: "color 0.2s, border-bottom 0.2s, opacity 0.2s",
      borderBottom: isActive ? "2px solid #7c3aed" : "2px solid transparent",
      marginTop: "40px",
      padding: "12px 20px",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      borderRadius: "8px",
      backgroundColor: disabled ? "#f5f5f5" : "#e6e6fa",
      opacity: disabled ? 0.5 : 1,
      ...style
    }}
  >
    <i className={icon} />
    {label}
  </span>
);

// Assignments List Component
const AssignmentsList = React.forwardRef(
  ({ assignments, selectedAssignment, onAssignmentSelect, isSticky }, ref) => (
    <div
      ref={ref}
      style={{
        width: "400px", // Fixed width
        height: "800px", // Fixed height to match content section
        padding: "24px",
        backgroundColor: "#ffffff",
        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
        border: "1px solid #e9ecef",
        borderRadius: "8px",
        position: isSticky ? "sticky" : "static",
        top: isSticky ? "120px" : "auto",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column"
      }}
    >
      {/* Section Header */}
      <div
        style={{
          marginTop: "-20px",
          marginLeft: "-20px",
          height: "130px",
          width: "390px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          paddingBottom: "16px",
          borderBottom: "2px solid #f8f9fa",
          background: "linear-gradient(90deg, #6366f1 0%, #a21caf 100%)",
          padding: "16px",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)"
        }}
      >
        <div>
          <h3
            style={{
              margin: 0,
              fontSize: "1.5rem",
              fontWeight: "700",
              color: "#ffffff",
              textTransform: "uppercase"
            }}
          >
            Content Modules
          </h3>
          <p
            style={{
              margin: "4px 0 0 0",
              fontSize: "1rem",
              color: "#ffffff",
              fontStyle: "italic"
            }}
          >
            Select a module to view content
          </p>
        </div>
        <span
          style={{
            fontSize: "1rem",
            color: "#6c757d",
            backgroundColor: "#f8f9fa",
            padding: "6px 14px",
            borderRadius: "20px",
            fontWeight: "500",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)"
          }}
        >
          {assignments.length} Modules
        </span>
      </div>
      <hr></hr>

      {/* Assignments List */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          flex: 1, // Take remaining space
          overflowY: "auto",
          scrollbarWidth: "thin",
          scrollbarColor: "#cbd5e0 #f7fafc",
          paddingRight: "8px" // Add padding for scrollbar
        }}
      >
        {assignments.map((assignment, index) => (
          <AssignmentCard
            key={assignment.id}
            assignment={assignment}
            index={index}
            isSelected={assignment.id === selectedAssignment?.id}
            onClick={() => onAssignmentSelect(assignment)}
            isLast={index === assignments.length - 1}
          />
        ))}
      </div>
    </div>
  )
);

// Assignment Card Component
const AssignmentCard = ({ assignment, index, isSelected, onClick, isLast }) => (
  <div
    onClick={onClick}
    style={{
      width: "100%", // Take full width of parent
      cursor: "pointer",
      padding: "16px 20px",
      backgroundColor: isSelected ? "#e3f2fd" : "#ffffff",
      transition: "all 0.3s ease",
      boxShadow: isSelected
        ? "0 4px 12px rgba(25, 118, 210, 0.15)"
        : "0 2px 4px rgba(0, 0, 0, 0.05)",
      position: 'relative',
      borderRadius: "8px",
      border: isSelected ? "2px solid #1976d2" : "1px solid #e0e0e0"
    }}
    onMouseEnter={(e) => {
      if (!isSelected) {
        e.currentTarget.style.backgroundColor = "#f8f9fa";
        e.currentTarget.style.transform = "translateY(-2px)";
      }
    }}
    onMouseLeave={(e) => {
      if (!isSelected) {
        e.currentTarget.style.backgroundColor = "#ffffff";
        e.currentTarget.style.transform = "translateY(0)";
      }
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <span
        style={{
          minWidth: "24px",
          height: "24px",
          borderRadius: "50%",
          backgroundColor: isSelected ? "#1976d2" : "#6c757d",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.75rem",
          fontWeight: "600",
        }}
      >
        {index + 1}
      </span>
      <span
        style={{
          fontWeight: isSelected ? "600" : "500",
          color: isSelected ? "#1565c0" : "#495057",
          fontSize: "0.95rem",
        }}
      >
        {assignment.title || "Untitled Assignment"}
      </span>
    </div>
  </div>
);

// ==================== UTILITY FUNCTIONS ====================
const buildBreadcrumb = (dayId, selectedAssignment) => {
  let breadcrumb = `Course / Day ${dayId}`;
  if (selectedAssignment) {
    breadcrumb += ` / ${selectedAssignment.title}`;
  }
  return breadcrumb;
};

export default DailyCourseModule;